import { DashboardLayout } from '@/layout/DashboardLayout';
import { AlertDialog, Button, Dialog, Input } from '@/design-system';
import type { Product } from '@/features/products';
import { subscriptionsService, type SubscriptionItemWithProduct } from '@/features/subscriptions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { planLicensingService } from '@/features/plans/api/plan-licensing.service';
import { canAddPlanProduct } from '@/features/plans/utils/product-plan-access';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { useSubscriptionItemsQuery } from '@/features/subscriptions/hooks/useSubscriptionItemsQuery';
import { useSubscriptionsByCompanyQuery } from '@/features/subscriptions/hooks/useSubscriptionsByCompanyQuery';
import { useActiveProductsCatalogQuery } from '@/features/products/hooks/useActiveProductsCatalogQuery';
import { useCompanyByIdQuery } from './hooks/useCompanyByIdQuery';
import { useCompanyDetailStatsQuery } from './hooks/useCompanyDetailStatsQuery';
import { ArrowLeft, BarChart3, CreditCard, Users, Plug, ToggleRight } from 'lucide-react';
import { CompanyDetailsHeader } from './components/CompanyDetailsHeader';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { cn } from '@/design-system/utils/cn';
import { companiesService, type Company } from './api/companies.service';
import { useAuth } from '@/core/auth';
import {
  ALICIA_TOKENS_FEATURE_KEY,
  AUDIO_TRANSCRIPTION_FEATURE_KEY,
  COMPANY_DETAIL_TABS,
  centsToBrlInput,
  formatPrice,
  intervalLabelPt,
} from './company-details-shared';
import { CompanyDetailsProvider, type CompanyDetailsOutletContext } from './company-details-context';
import { subscriptionEstimate as estimateSubscription } from './utils/subscription-estimate';

/** Abas que o perfil Suporte enxerga: visão geral, PH24 e Integrações (é quem reverte um entitlement desligado). */
const SUPPORT_VISIBLE_TAB_SEGMENTS: readonly string[] = ['overview', 'ph24', 'integrations'];

export function CompanyDetailsLayout() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user: staffUser } = useAuth();
  const isSupport = staffUser?.role === 'SUPPORT';
  const visibleTabs = isSupport
    ? COMPANY_DETAIL_TABS.filter((tab) => SUPPORT_VISIBLE_TAB_SEGMENTS.includes(tab.segment))
    : COMPANY_DETAIL_TABS;
  const [accessTokenLoading, setAccessTokenLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [addProductId, setAddProductId] = useState('');
  const [addQty, setAddQty] = useState(1);
  const [addonPricingMode, setAddonPricingMode] = useState<'catalog' | 'free' | 'custom'>('catalog');
  const [addonCustomPriceBrl, setAddonCustomPriceBrl] = useState('');
  const [modulePricingMode, setModulePricingMode] = useState<'catalog' | 'free' | 'custom'>('catalog');
  const [moduleCustomPriceBrl, setModuleCustomPriceBrl] = useState('');
  const [billingProrateAsaas, setBillingProrateAsaas] = useState(false);
  const [alert, setAlert] = useState<{ show: boolean; message: string }>({ show: false, message: '' });
  const [aliciaBonusDelta, setAliciaBonusDelta] = useState('');
  const [transcriptionBonusDelta, setTranscriptionBonusDelta] = useState('');
  const [bonusSubmitting, setBonusSubmitting] = useState<'alicia' | 'transcription' | null>(null);
  const [showEraseFinancialDialog, setShowEraseFinancialDialog] = useState(false);
  const [financialEraseSlug, setFinancialEraseSlug] = useState('');
  const [eraseFinancialLoading, setEraseFinancialLoading] = useState(false);
  const [showDestroyShellDialog, setShowDestroyShellDialog] = useState(false);
  const [destroyShellSlug, setDestroyShellSlug] = useState('');
  const [destroyShellPhrase, setDestroyShellPhrase] = useState('');
  const [destroyShellLoading, setDestroyShellLoading] = useState(false);
  const [editItem, setEditItem] = useState<SubscriptionItemWithProduct | null>(null);
  const [editItemPriceBrl, setEditItemPriceBrl] = useState('');
  const [editItemQty, setEditItemQty] = useState('1');
  const [editItemLoading, setEditItemLoading] = useState(false);

  const companyQuery = useCompanyByIdQuery(id);
  const subsQuery = useSubscriptionsByCompanyQuery(id);
  const statsQuery = useCompanyDetailStatsQuery(id);
  const licensingQuery = useQuery({ queryKey: queryKeys.companies.licensing(id ?? ''), queryFn: () => planLicensingService.company(id!), enabled: !!id });
  const capabilitiesQuery = useQuery({ queryKey: queryKeys.plans.capabilities, queryFn: planLicensingService.catalog, enabled: !!id });

  const company = companyQuery.data ?? null;
  const subscriptions = useMemo(() => subsQuery.data?.data ?? [], [subsQuery.data]);
  const stats = statsQuery.data ?? null;

  const activeSubscription = useMemo(
    () => subscriptions.find((s) => s.status === 'ACTIVE' || s.status === 'TRIALING') ?? null,
    [subscriptions]
  );
  const displaySubscription = useMemo(
    () => activeSubscription ?? subscriptions[0] ?? null,
    [activeSubscription, subscriptions]
  );

  const itemsQuery = useSubscriptionItemsQuery(displaySubscription?.id, !!displaySubscription?.id);
  const subscriptionItems = useMemo(() => itemsQuery.data ?? [], [itemsQuery.data]);
  const productsQuery = useActiveProductsCatalogQuery(!!activeSubscription && !!displaySubscription);
  const products = useMemo<Product[]>(() => productsQuery.data ?? [], [productsQuery.data]);
  const loadingItems = itemsQuery.isLoading || (productsQuery.isFetching && !!activeSubscription);

  const loading = companyQuery.isLoading || subsQuery.isLoading;
  const statsRefreshing = statsQuery.isFetching && !statsQuery.isLoading;

  const refreshStats = () => {
    if (!id) return;
    void statsQuery.refetch();
  };

  useEffect(() => {
    if (!id) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.companies.detailStats(id) });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [id, queryClient]);

  const invalidateCompanyDetail = useCallback(() => {
    if (!id) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.companies.detail(id) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.companies.detailStats(id) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.byCompany(id) });
    if (displaySubscription) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.items(displaySubscription.id) });
    }
  }, [id, displaySubscription, queryClient]);

  const refreshItems = useCallback(() => {
    if (!displaySubscription) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.items(displaySubscription.id) });
  }, [displaySubscription, queryClient]);

  useEffect(() => {
    if (itemsQuery.isError && itemsQuery.error) {
      setAlert({
        show: true,
        message: itemsQuery.error instanceof Error ? itemsQuery.error.message : 'Erro ao carregar itens da assinatura',
      });
    }
  }, [itemsQuery.isError, itemsQuery.error]);

  useEffect(() => {
    if (companyQuery.isError && companyQuery.error) {
      setAlert({
        show: true,
        message: companyQuery.error instanceof Error ? companyQuery.error.message : 'Erro ao carregar empresa',
      });
    }
  }, [companyQuery.isError, companyQuery.error]);

  const parseBrlToCents = (input: string): number => {
    const cleaned = input.replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, '');
    const num = parseFloat(cleaned || '0');
    return Math.round(num * 100);
  };

  const buildPriceOpts = (
    mode: 'catalog' | 'free' | 'custom',
    customBrl: string
  ): { ok: true; opts?: { unitPriceCents: number } } | { ok: false } => {
    if (mode === 'catalog') return { ok: true };
    if (mode === 'free') return { ok: true, opts: { unitPriceCents: 0 } };
    const cents = parseBrlToCents(customBrl);
    if (!Number.isFinite(cents) || cents < 0) return { ok: false };
    return { ok: true, opts: { unitPriceCents: cents } };
  };

  const handleAccessCompany = async () => {
    if (!id || !company) return;
    setAccessTokenLoading(true);
    try {
      const result = await companiesService.generateAccessToken(id);
      const appUrl = import.meta.env.VITE_APP_URL || 'https://app.oryacrm.com';
      const url = `${appUrl}/auth/staff-access?token=${encodeURIComponent(result.accessToken)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao gerar token de acesso';
      setAlert({ show: true, message });
    } finally {
      setAccessTokenLoading(false);
    }
  };

  const handleAddModule = async (productId: string) => {
    if (!activeSubscription) return;
    const built = buildPriceOpts(modulePricingMode, moduleCustomPriceBrl);
    if (!built.ok) {
      setAlert({ show: true, message: 'Informe um preço unitário válido (R$).' });
      return;
    }
    setActionLoading(productId);
    try {
      await subscriptionsService.addItem(
        activeSubscription.id,
        productId,
        1,
        {
          ...(built.opts?.unitPriceCents !== undefined ? { unitPriceCents: built.opts.unitPriceCents } : {}),
          ...(billingProrateAsaas ? { prorate: true } : {}),
        }
      );
      refreshItems();
      setModulePricingMode('catalog');
      setModuleCustomPriceBrl('');
    } catch (e: unknown) {
      setAlert({ show: true, message: e instanceof Error ? e.message : 'Erro ao habilitar módulo' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveModule = async (itemId: string) => {
    setActionLoading(itemId);
    try {
      await subscriptionsService.removeItem(itemId, billingProrateAsaas ? { prorate: true } : undefined);
      refreshItems();
    } catch (e: unknown) {
      setAlert({ show: true, message: e instanceof Error ? e.message : 'Erro ao desabilitar módulo' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddAdicional = async (productId: string, quantity: number) => {
    if (!activeSubscription || quantity < 1) return;
    const built = buildPriceOpts(addonPricingMode, addonCustomPriceBrl);
    if (!built.ok) {
      setAlert({ show: true, message: 'Informe um preço unitário válido (R$).' });
      return;
    }
    setActionLoading(productId);
    try {
      await subscriptionsService.addItem(
        activeSubscription.id,
        productId,
        quantity,
        {
          ...(built.opts?.unitPriceCents !== undefined ? { unitPriceCents: built.opts.unitPriceCents } : {}),
          ...(billingProrateAsaas ? { prorate: true } : {}),
        }
      );
      refreshItems();
      setAddProductId('');
      setAddQty(1);
      setAddonPricingMode('catalog');
      setAddonCustomPriceBrl('');
    } catch (e: unknown) {
      setAlert({ show: true, message: e instanceof Error ? e.message : 'Erro ao adicionar item' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveAdicional = async (itemId: string) => {
    setActionLoading(itemId);
    try {
      await subscriptionsService.removeItem(itemId, billingProrateAsaas ? { prorate: true } : undefined);
      refreshItems();
    } catch (e: unknown) {
      setAlert({ show: true, message: e instanceof Error ? e.message : 'Erro ao remover item' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddAliciaBonus = async () => {
    if (!id) return;
    const n = Number(aliciaBonusDelta);
    if (!Number.isFinite(n) || n === 0) {
      setAlert({ show: true, message: 'Informe um número diferente de zero para adicionar tokens.' });
      return;
    }
    setBonusSubmitting('alicia');
    try {
      const data = await companiesService.addAliciaTokenBonus(id, Math.round(n));
      setAliciaBonusDelta('');
      setAlert({
        show: true,
        message: `Bônus Alici.IA atualizado. Total acumulado: ${data.bonusTokens.toLocaleString('pt-BR')} tokens.`,
      });
      invalidateCompanyDetail();
    } catch (e: unknown) {
      setAlert({ show: true, message: e instanceof Error ? e.message : 'Erro ao adicionar bônus' });
    } finally {
      setBonusSubmitting(null);
    }
  };

  const handleAddTranscriptionBonus = async () => {
    if (!id) return;
    const n = Number(transcriptionBonusDelta);
    if (!Number.isFinite(n) || n === 0) {
      setAlert({ show: true, message: 'Informe um número diferente de zero para adicionar minutos.' });
      return;
    }
    setBonusSubmitting('transcription');
    try {
      const data = await companiesService.addTranscriptionMinutesBonus(id, Math.round(n));
      setTranscriptionBonusDelta('');
      setAlert({
        show: true,
        message: `Bônus de transcrição atualizado. Total acumulado: ${data.bonusMinutes.toLocaleString('pt-BR')} min.`,
      });
      invalidateCompanyDetail();
    } catch (e: unknown) {
      setAlert({ show: true, message: e instanceof Error ? e.message : 'Erro ao adicionar bônus' });
    } finally {
      setBonusSubmitting(null);
    }
  };

  const handleEraseFinancialHistory = async () => {
    if (!id || !company) return;
    setEraseFinancialLoading(true);
    try {
      await companiesService.eraseFinancialHistory(id, financialEraseSlug);
      setShowEraseFinancialDialog(false);
      setFinancialEraseSlug('');
      setAlert({
        show: true,
        message:
          'Histórico financeiro removido do Orya nesta shell (assinaturas, itens e gateway). Você permanece nesta página — atualize os dados abaixo. O painel do gateway (ex.: Asaas) não é alterado.',
      });
      invalidateCompanyDetail();
    } catch (e: unknown) {
      setAlert({
        show: true,
        message: e instanceof Error ? e.message : 'Erro ao apagar histórico financeiro',
      });
    } finally {
      setEraseFinancialLoading(false);
    }
  };

  const handleDestroyPurgedShell = async () => {
    if (!id || !company) return;
    setDestroyShellLoading(true);
    try {
      await companiesService.destroyPurgedShell(id, destroyShellSlug, 'SEM RASTRO NO ORYA');
      setShowDestroyShellDialog(false);
      setDestroyShellSlug('');
      setDestroyShellPhrase('');
      setAlert({
        show: true,
        message:
          'Empresa removida do Orya (sem linha em companies). Você será redirecionado à lista. Dados em gateways externos (ex.: Asaas) não são apagados por esta ação.',
      });
      setTimeout(() => navigate('/companies'), 800);
    } catch (e: unknown) {
      setAlert({
        show: true,
        message: e instanceof Error ? e.message : 'Erro ao excluir shell',
      });
    } finally {
      setDestroyShellLoading(false);
    }
  };

  const openEditItemDialog = (item: SubscriptionItemWithProduct) => {
    setEditItem(item);
    setEditItemPriceBrl(centsToBrlInput(item.priceAtMoment));
    setEditItemQty(String(item.quantity));
  };

  const closeEditItemDialog = () => {
    if (editItemLoading) return;
    setEditItem(null);
    setEditItemPriceBrl('');
    setEditItemQty('1');
  };

  const handleSubmitEditItem = async () => {
    if (!editItem) return;
    const cents = parseBrlToCents(editItemPriceBrl);
    if (!Number.isFinite(cents) || cents < 0) {
      setAlert({ show: true, message: 'Informe um preço unitário válido (R$).' });
      return;
    }
    const qtyRaw = parseInt(editItemQty, 10);
    const qty = editItem.product.isModule ? 1 : Math.max(1, Number.isFinite(qtyRaw) ? qtyRaw : 1);

    setEditItemLoading(true);
    try {
      await subscriptionsService.updateItem(editItem.id, {
        unitPriceCents: cents,
        ...(editItem.product.isModule ? {} : { quantity: qty }),
        ...(billingProrateAsaas ? { prorate: true } : {}),
      });
      refreshItems();
      closeEditItemDialog();
      setAlert({
        show: true,
        message:
          'Preço do item atualizado. O total da assinatura foi recalculado e sincronizado com o gateway quando houver assinatura vinculada.',
      });
    } catch (e: unknown) {
      setAlert({
        show: true,
        message: e instanceof Error ? e.message : 'Erro ao atualizar item',
      });
    } finally {
      setEditItemLoading(false);
    }
  };

  const statusLabel: Record<string, string> = {
    ACTIVE: 'Ativa',
    TRIALING: 'Em trial',
    PAST_DUE: 'Em atraso',
    CANCELED: 'Cancelada',
  };

  const companyStatusPt = (c: Company) => {
    const st = (c as { status?: string }).status;
    if (st === 'PURGED') return 'Arquivo financeiro (purgada)';
    if (c.deletedAt) return 'Arquivada (restaurável)';
    return 'Ativa';
  };

  const gatewayShort = (raw: string | null | undefined) => {
    if (!raw) return '—';
    if (raw.length <= 12) return raw;
    return `…${raw.slice(-10)}`;
  };

  const isPurged = company ? (company as { status?: string }).status === 'PURGED' : false;
  const canManageSubscription = !!activeSubscription && !isPurged;
  const hasFinancialArchiveInOrya =
    isPurged &&
    (subscriptions.length > 0 ||
      !!(company && (company.gatewayCustomerId || company.gatewayProvider)));

  const subscriptionEstimate = useMemo(() => {
    if (licensingQuery.isError || licensingQuery.isPending || itemsQuery.isError || itemsQuery.isLoading) return null;
    return estimateSubscription(licensingQuery.data, displaySubscription, subscriptionItems);
  }, [licensingQuery.data, licensingQuery.isError, licensingQuery.isPending, itemsQuery.isError, itemsQuery.isLoading, displaySubscription, subscriptionItems]);

  const limits = stats?.limits ?? displaySubscription?.plan?.limits ?? {};
  const usersLimit = licensingQuery.data?.mode === 'ENFORCED' ? licensingQuery.data.current?.assignment.seatCount ?? null : limits.users ?? null;
  const licensed = licensingQuery.data?.mode === 'ENFORCED' ? licensingQuery.data.current : undefined;
  const waLimit = licensingQuery.data?.mode === 'ENFORCED'
    ? licensed ? (licensed.plan.limits?.whatsapp_connections ?? 1) + (licensed.assignment.additionalConnectionCount ?? 0) : 0
    : limits.whatsapp_connections ?? null;
  const usedUsers = licensingQuery.data?.mode === 'ENFORCED' ? licensingQuery.data.usedSeats : stats?.activeUsers ?? 0;
  const usedWa = stats?.whatsappConnections ?? 0;
  const usersUsageAvailable = licensingQuery.data?.mode === 'ENFORCED'
    ? !licensingQuery.isError : !!stats && !statsQuery.isError;
  const channelsUsageAvailable = !!stats && !statsQuery.isError;
  const transcriptionMinutesUsed = stats?.transcriptionMinutesUsed ?? 0;
  const transcriptionMinutesLimit = stats?.transcriptionMinutesLimit ?? 0;
  const transcriptionUsagePct =
    transcriptionMinutesLimit > 0
      ? Math.min(100, Math.round((transcriptionMinutesUsed / transcriptionMinutesLimit) * 100))
      : null;

  const transcriptionItem = subscriptionItems.find((i) => i.product.featureKey === AUDIO_TRANSCRIPTION_FEATURE_KEY);
  const aliciaItem = subscriptionItems.find((i) => i.product.featureKey === ALICIA_TOKENS_FEATURE_KEY);

  const planCardStatus = displaySubscription?.status
    ? statusLabel[displaySubscription.status] ?? displaySubscription.status
    : 'Sem assinatura';

  const modules = useMemo(
    () => subscriptionItems.filter((i) => i.product.isModule),
    [subscriptionItems]
  );
  const adicionais = useMemo(
    () => subscriptionItems.filter((i) => !i.product.isModule),
    [subscriptionItems]
  );
  const availableModules = useMemo(
    () => products.filter((p) => p.isModule && canAddPlanProduct(p, licensingQuery.data, capabilitiesQuery.data) && !subscriptionItems.some((i) => i.productId === p.id)),
    [products, subscriptionItems, licensingQuery.data, capabilitiesQuery.data]
  );
  const availableAdicionais = useMemo(() => products.filter((p) => !p.isModule && canAddPlanProduct(p, licensingQuery.data, capabilitiesQuery.data)), [products, licensingQuery.data, capabilitiesQuery.data]);

  if (!id) {
    return <Navigate to="/companies" replace />;
  }

  if (isSupport && !visibleTabs.some((tab) => location.pathname.endsWith(`/${tab.segment}`))) {
    return <Navigate to={`/companies/${id}/overview`} replace />;
  }

  if (loading && !company) {
    return (
      <DashboardLayout title="Carregando…">
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0084FF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!company) {
    return (
      <DashboardLayout title="Empresa não encontrada">
        <div className="text-center py-12">
          <p className="text-gray-600">Empresa não encontrada ou sem permissão para visualizar.</p>
          <Link to="/companies" className="mt-4 inline-flex items-center gap-2 text-[#0084FF] hover:underline">
            <ArrowLeft size={18} />
            Voltar à lista de empresas
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const outletCtx: CompanyDetailsOutletContext = {
    id,
    company,
    navigate,
    staffUser,
    accessTokenLoading,
    handleAccessCompany,
    companyStatusPt,
    gatewayShort,
    isPurged,
    hasFinancialArchiveInOrya,
    setShowEraseFinancialDialog,
    setShowDestroyShellDialog,
    displaySubscription,
    subscriptions,
    statusLabel,
    planCardStatus,
    currentPlanName: licensingQuery.isError ? null : licensingQuery.data?.current?.plan.name ?? (licensingQuery.data?.mode === 'LEGACY' ? displaySubscription?.plan?.name : null),
    formatPrice,
    subscriptionEstimate,
    intervalLabelPt,
    statsRefreshing,
    refreshStats,
    usedUsers,
    usersUsageAvailable,
    channelsUsageAvailable,
    usersLimit,
    usedWa,
    waLimit,
    transcriptionMinutesUsed,
    transcriptionMinutesLimit,
    transcriptionUsagePct,
    transcriptionItem,
    aliciaItem,
    aliciaBonusDelta,
    setAliciaBonusDelta,
    transcriptionBonusDelta,
    setTranscriptionBonusDelta,
    bonusSubmitting,
    handleAddAliciaBonus,
    handleAddTranscriptionBonus,
    billingProrateAsaas,
    setBillingProrateAsaas,
    canManageSubscription,
    loadingItems,
    activeSubscription,
    modules,
    adicionais,
    availableModules,
    availableAdicionais,
    handleAddModule,
    handleRemoveModule,
    openEditItemDialog,
    handleRemoveAdicional,
    handleAddAdicional,
    modulePricingMode,
    setModulePricingMode,
    moduleCustomPriceBrl,
    setModuleCustomPriceBrl,
    addonPricingMode,
    setAddonPricingMode,
    addonCustomPriceBrl,
    setAddonCustomPriceBrl,
    addProductId,
    setAddProductId,
    addQty,
    setAddQty,
    actionLoading,
    editItemLoading,
  };

  return (
    <CompanyDetailsProvider value={outletCtx}>
      <DashboardLayout title={company.name} noContentPadding>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          <nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-gray-200 bg-white lg:w-52 lg:flex-col lg:border-b-0 lg:border-r" aria-label="Secções da empresa">
            {visibleTabs.map(({ segment, label }) => (
              <NavLink
                key={segment}
                to={segment}
                className={({ isActive }) =>
                  cn(
                    'flex shrink-0 items-center gap-2 rounded-none px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive ? 'bg-primary/10 text-primary' : 'text-gray-600 hover:bg-gray-50'
                  )
                }
              >
                {segment === 'overview' ? <BarChart3 size={17} /> : segment === 'plans' ? <CreditCard size={17} /> : segment === 'members' ? <Users size={17} /> : segment === 'integrations' ? <ToggleRight size={17} /> : <Plug size={17} />}{label}
              </NavLink>
            ))}
          </nav>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <CompanyDetailsHeader />
            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-4 md:p-6"><Outlet /></div>
          </div>
        </div>

        <Dialog
          isOpen={!!editItem}
          onClose={closeEditItemDialog}
          title="Ajustar item da assinatura"
          size="md"
          footer={editItem ? (
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={closeEditItemDialog} disabled={editItemLoading}>Cancelar</Button>
              <Button variant="primary" onClick={() => void handleSubmitEditItem()} disabled={editItemLoading} isLoading={editItemLoading}>Salvar</Button>
            </div>
          ) : undefined}
        >
          {editItem ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">{editItem.product.name}</span>
                <span className="block mt-1 text-xs text-gray-500">
                  O total mensal da assinatura usa <strong>preço unitário × quantidade</strong> de cada item. Valor{' '}
                  <strong>0,00</strong> = cortesia neste item. Com assinatura vinculada ao gateway, o total é
                  sincronizado após salvar.
                </span>
              </p>
              <Input
                label="Preço unitário (R$)"
                value={editItemPriceBrl}
                onChange={(e) => setEditItemPriceBrl(e.target.value)}
                placeholder="Ex: 29,90 ou 0"
                autoComplete="off"
              />
              {!editItem.product.isModule ? (
                <Input
                  label="Quantidade"
                  type="number"
                  min={1}
                  value={editItemQty}
                  onChange={(e) => setEditItemQty(e.target.value)}
                />
              ) : (
                <p className="text-xs text-gray-500">Módulos permanecem com quantidade 1.</p>
              )}
            </div>
          ) : null}
        </Dialog>

        <Dialog
          isOpen={showEraseFinancialDialog}
          onClose={() => {
            if (!eraseFinancialLoading) {
              setShowEraseFinancialDialog(false);
              setFinancialEraseSlug('');
            }
          }}
          title="Confirmar exclusão do histórico financeiro"
          size="md"
          footer={(
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => { setShowEraseFinancialDialog(false); setFinancialEraseSlug(''); }} disabled={eraseFinancialLoading}>Cancelar</Button>
              <Button variant="danger" onClick={() => void handleEraseFinancialHistory()} disabled={eraseFinancialLoading || !financialEraseSlug.trim()} isLoading={eraseFinancialLoading}>Apagar no Orya</Button>
            </div>
          )}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Esta ação é <strong>irreversível</strong> no banco do Orya. Digite o <strong>slug</strong> da empresa para
              confirmar:
            </p>
            <p className="text-xs font-mono bg-gray-100 px-2 py-1 rounded border border-gray-200">{company.slug}</p>
            <Input
              label="Slug (cópia exata)"
              value={financialEraseSlug}
              onChange={(e) => setFinancialEraseSlug(e.target.value)}
              placeholder="ex: purged-..."
              autoComplete="off"
            />
          </div>
        </Dialog>

        <Dialog
          isOpen={showDestroyShellDialog}
          onClose={() => {
            if (!destroyShellLoading) {
              setShowDestroyShellDialog(false);
              setDestroyShellSlug('');
              setDestroyShellPhrase('');
            }
          }}
          title="Confirmar exclusão total no Orya"
          size="md"
          footer={(
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => { setShowDestroyShellDialog(false); setDestroyShellSlug(''); setDestroyShellPhrase(''); }} disabled={destroyShellLoading}>Cancelar</Button>
              <Button variant="danger" onClick={() => void handleDestroyPurgedShell()} disabled={destroyShellLoading || !destroyShellSlug.trim() || destroyShellPhrase.trim() !== 'SEM RASTRO NO ORYA'} isLoading={destroyShellLoading}>Excluir do Orya</Button>
            </div>
          )}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Esta ação <strong>remove a empresa do banco do Orya</strong>. Digite o <strong>slug</strong> e, em
              seguida, a frase exata abaixo.
            </p>
            <p className="text-xs font-mono bg-gray-100 px-2 py-1 rounded border border-gray-200">{company.slug}</p>
            <Input
              label="Slug (cópia exata)"
              value={destroyShellSlug}
              onChange={(e) => setDestroyShellSlug(e.target.value)}
              placeholder="ex: purged-..."
              autoComplete="off"
            />
            <p className="text-sm text-gray-600">
              Frase (maiúsculas):{' '}
              <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">SEM RASTRO NO ORYA</code>
            </p>
            <Input
              label="Confirmação da frase"
              value={destroyShellPhrase}
              onChange={(e) => setDestroyShellPhrase(e.target.value)}
              placeholder="SEM RASTRO NO ORYA"
              autoComplete="off"
            />
          </div>
        </Dialog>

        <AlertDialog
          isOpen={alert.show}
          onClose={() => setAlert({ show: false, message: '' })}
          title="Aviso"
          message={alert.message}
          variant="success"
        />
      </DashboardLayout>
    </CompanyDetailsProvider>
  );
}
